import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, List as ListIcon, Map as MapIcon, RefreshCw, Camera, ExternalLink, Route as RouteIcon, X, ChevronDown, ChevronUp } from 'lucide-react';
import { fetchWithTimeout } from '../../utils/network.utils';
import { AuthContext } from '../../contexts/AuthContext';
import { Portal } from '../Portal';
import PhotoLightbox from '../shared/PhotoLightbox';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';
mapboxgl.accessToken = MAPBOX_TOKEN;

// How far BEHIND live the smoothed markers play back (Option 2). A bit larger
// than the fix cadence so the next fix is always buffered before a marker reaches
// the current one — that's what keeps motion continuous. Bump up if pauses show.
const PLAYBACK_DELAY_MS = 11000;

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const authHeaders = (): Record<string, string> => ({ Authorization: `Bearer ${localStorage.getItem('bfa_token')}` });
const api = async (path: string, opts: any = {}) => {
  const res = await fetchWithTimeout(`${API_BASE_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  return res.json();
};

// ── Types (mirror /api/fleet-vehicles) ───────────────────────────────────────
interface Vehicle {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  equipment_type: string;
  is_hauler: boolean;
  source: 'traccar' | 'samsara' | 'both';
  position_source: 'traccar' | 'samsara' | null;
  samsara_vehicle_id: string | null;
  has_camera: boolean;
  lat: number | null;
  lng: number | null;
  speed_mph: number | null;
  heading: number | null;
  ignition_on: boolean | null;
  address: string | null;
  synced_at: string | null;
  status: 'moving' | 'idle' | 'parked' | 'offline';
  assigned_to: { employee_id: number; first_name: string; name: string } | null;
  driver: { employee_id: number; name: string } | null;
  current_job: { job_id: number; job_name: string; bradanna_job_number: string | null } | null;
  current_place: { place_id: number; name: string; category: string | null } | null;
  parked_minutes: number | null;
  ran_today_minutes: number | null;   // minutes run today (engine-on/moving) — drives the Active Today card
  loaded_equipment: { equipment_id: number; equipment_name: string; bradanna_equipment_number: string | null; equipment_type: string }[] | null;
}
interface Totals { count: number; moving: number; idle: number; parked: number; offline: number; with_camera: number }
interface Trip { start_time: string; end_time: string; start_address: string | null; end_address: string | null; from?: string | null; to?: string | null; distance_miles: number; duration_min: number; max_speed_mph: number; path: [number, number][] }
interface Stop { lat: number; lng: number; address: string | null; arrival: string; departure: string; duration_min: number; place?: { place_id: number; name: string; category: string | null } | null; job?: { job_id: number; job_name: string } | null }
interface TripData { equipment_id: number; equipment_name: string; date: string; source: string | null; point_count: number; points: any[]; trips: Trip[]; stops: Stop[]; total_distance_miles: number; total_drive_min: number; total_stop_min: number }
interface JobGeofence { job_id: number; job_name: string; lat: number; lng: number; geofence_type: 'circle' | 'polygon' | null; geofence_data: any }

const STATUS_COLOR: Record<Vehicle['status'], string> = { moving: '#10b981', idle: '#f59e0b', parked: '#64748b', offline: '#94a3b8' };
const SOURCE_LABEL: Record<Vehicle['source'], string> = { both: 'Samsara + GPS', traccar: 'GPS Tracker', samsara: 'Samsara' };

const formatAge = (iso: string | null) => {
  if (!iso) return '—';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return `${Math.floor(hr / 24)} d ago`;
};
const formatClock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
const formatDur = (min: number) => (min < 60 ? `${min}m` : `${Math.floor(min / 60)}h ${min % 60}m`);
// Caption for a camera frame in the photo viewer — when it was taken + how long ago.
const photoCaption = (c: { startTime?: string; capturedAtTime?: string }) => {
  const t = c.startTime || c.capturedAtTime;
  return t ? `Taken ${formatClock(t)} · ${formatAge(t)}` : null;
};
const todayEstYmd = () => {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const g = (t: string) => p.find(x => x.type === t)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')}`;
};
const displayName = (v: Vehicle) => v.assigned_to?.first_name || v.equipment_name;

function circleToPolygon(lat: number, lng: number, radiusM: number, n = 40): [number, number][] {
  const coords: [number, number][] = [], R = 6371000, latRad = (lat * Math.PI) / 180;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * 2 * Math.PI, dx = (radiusM * Math.cos(a)) / R, dy = (radiusM * Math.sin(a)) / R;
    coords.push([lng + ((dy * 180) / Math.PI) / Math.cos(latRad), lat + (dx * 180) / Math.PI]);
  }
  return coords;
}
function buildJobFCs(jobs: JobGeofence[]) {
  const polys: any[] = [], labels: any[] = [];
  for (const j of jobs) {
    let ring: [number, number][] | null = null;
    if (j.geofence_type === 'polygon' && Array.isArray(j.geofence_data?.points)) {
      ring = j.geofence_data.points.map((p: any) => [p.lng, p.lat]);
      if (ring && ring.length) ring.push(ring[0]);
    } else {
      ring = circleToPolygon(j.lat, j.lng, Number(j.geofence_data?.radius) || 100);
    }
    if (ring) polys.push({ type: 'Feature', properties: { name: j.job_name }, geometry: { type: 'Polygon', coordinates: [ring] } });
    labels.push({ type: 'Feature', properties: { name: j.job_name }, geometry: { type: 'Point', coordinates: [j.lng, j.lat] } });
  }
  return { polys: { type: 'FeatureCollection' as const, features: polys }, labels: { type: 'FeatureCollection' as const, features: labels } };
}

// Fan trucks that are stacked at the same spot (e.g. several at the shop) so
// their labels don't overlap. Returns display [lng,lat] per truck — a ring sized
// in pixels at the current zoom so separation stays consistent. Non-stacked
// trucks keep their true position.
const SPREAD_RADIUS_M = 70;
const SPREAD_PX = 52;
const SPREAD_MIN_ZOOM = 13;   // fan stacked trucks (dot + leader to label) sooner, like Fleet
function spreadPositions(vehicles: Vehicle[], zoom: number): Map<number, { lng: number; lat: number; labelLeft: boolean }> {
  const out = new Map<number, { lng: number; lat: number; labelLeft: boolean }>();
  const valid = vehicles.filter(v => v.lat != null && v.lng != null);
  const dist = (a: Vehicle, b: Vehicle) => {
    const R = 6371000, toR = (d: number) => d * Math.PI / 180;
    const dLat = toR((b.lat as number) - (a.lat as number)), dLng = toR((b.lng as number) - (a.lng as number));
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat as number)) * Math.cos(toR(b.lat as number)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
  };
  const assigned = new Set<number>();
  const groups: Vehicle[][] = [];
  for (let i = 0; i < valid.length; i++) {
    if (assigned.has(i)) continue;
    const g = [valid[i]]; assigned.add(i);
    for (let j = i + 1; j < valid.length; j++) {
      if (!assigned.has(j) && dist(valid[i], valid[j]) < SPREAD_RADIUS_M) { g.push(valid[j]); assigned.add(j); }
    }
    groups.push(g);
  }
  for (const g of groups) {
    if (g.length === 1) { out.set(g[0].equipment_id, { lng: g[0].lng as number, lat: g[0].lat as number, labelLeft: false }); continue; }
    const sorted = [...g].sort((a, b) => a.equipment_id - b.equipment_id);
    const lat0 = sorted[0].lat as number;
    // Offset every clustered truck from the group CENTROID (not its own GPS) so the
    // fan is a true circle instead of a scatter, matching the Fleet page.
    const cLat = sorted.reduce((s, v) => s + (v.lat as number), 0) / sorted.length;
    const cLng = sorted.reduce((s, v) => s + (v.lng as number), 0) / sorted.length;
    // Clamp the offset's zoom basis to 14 so fanning at a low zoom doesn't throw
    // markers kilometers apart (matches FleetView) — keeps the fan tight.
    const mPerPx = 156543.03 * Math.cos(lat0 * Math.PI / 180) / Math.pow(2, Math.max(zoom, 14));
    const degR = ((SPREAD_PX / 2) * Math.max(1, Math.log2(sorted.length + 1)) * mPerPx) / 111000;
    const cosLat = Math.max(0.1, Math.cos(lat0 * Math.PI / 180));
    sorted.forEach((v, k) => {
      // Half-step rotation (+PI/n) so no truck sits dead-top — leaves a gap for the
      // job-name label. labelLeft = west half → label hangs outward to the left.
      const ang = (k / sorted.length) * 2 * Math.PI + Math.PI / 2 + Math.PI / sorted.length;
      out.set(v.equipment_id, { lng: cLng + degR * Math.cos(ang) / cosLat, lat: cLat + degR * Math.sin(ang), labelLeft: Math.cos(ang) < 0 });
    });
  }
  return out;
}

const TRUCK_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>`;
function markerSig(v: Vehicle, zoomedIn: boolean, dotOnly: boolean, fanned: boolean): string {
  // Raw speed is deliberately NOT in the signature — it changes every fix and
  // would rebuild the label DOM each time (a flash). Only the hauler mph-badge's
  // PRESENCE is tracked; its number is updated in place in the markers effect.
  const mphPresent = v.is_hauler && (v.speed_mph ?? 0) > 0 ? 'm' : '';
  return [v.is_hauler ? 'h' : 't', zoomedIn ? 'z' : '', dotOnly ? 'd' : '', fanned ? 'f' : '', v.status, mphPresent, displayName(v), v.current_place?.name ?? '', (v.loaded_equipment || []).map(m => m.equipment_id).join(',')].join('|');
}
function markerHTML(v: Vehicle, zoomedIn: boolean, dotOnly: boolean, fanned: boolean): string {
  const primary = displayName(v);
  // Samsara-style declutter: when labels would collide (zoomed out), show only a
  // bare dot — no name, no big truck icon. The name returns on zoom-in.
  if (dotOnly) {
    if (v.is_hauler) {
      const aboard = v.loaded_equipment?.length || 0;
      const badge = aboard > 0 ? `<span style="position:absolute;top:-4px;right:-4px;background:#dc2626;color:#fff;font-size:10px;font-weight:700;border-radius:9px;padding:1px 5px;border:1.5px solid #fff;line-height:1;">${aboard}</span>` : '';
      return `<span style="position:relative;width:14px;height:14px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;">${badge}</span>`;
    }
    return `<span style="width:13px;height:13px;border-radius:50%;background:${STATUS_COLOR[v.status]};border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;"></span>`;
  }
  const placeName = v.current_place?.name;
  const sub = placeName ? `📍 ${placeName}` : (v.assigned_to ? '' : (v.bradanna_equipment_number || ''));
  if (v.is_hauler) {
    const aboard = v.loaded_equipment?.length || 0, mph = v.speed_mph ?? 0;
    const lines = (v.loaded_equipment || []).map(m => `<div style="font-weight:600;white-space:nowrap;">${m.equipment_name}${m.bradanna_equipment_number ? ` <span style="opacity:0.65;font-weight:500">${m.bradanna_equipment_number}</span>` : ''}</div>`).join('');
    const countBadge = aboard > 0 ? `<span style="position:absolute;top:-4px;right:-4px;background:#dc2626;color:#fff;font-size:10px;font-weight:700;border-radius:9px;padding:1px 5px;border:1.5px solid #fff;line-height:1;">${aboard}</span>` : '';
    // Zoomed in: a small orange dot (the big truck icon overwhelms a tight view).
    // Zoomed out: the big truck icon so haulers are easy to pick out.
    // Fanned: no icon — the leader line already ends in a dot at the true GPS, so a
    // second orange dot on the label reads as two markers.
    const icon = fanned
      ? ''
      : zoomedIn
      ? `<span style="position:relative;width:16px;height:16px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;">${countBadge}</span>`
      : `<span style="position:relative;width:32px;height:32px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;flex-shrink:0;">${TRUCK_SVG}${countBadge}</span>`;
    return `
      ${icon}
      <div style="background:rgba(245,158,11,0.95);color:#0f172a;padding:4px 8px;border-radius:6px;font-size:12px;font-weight:700;line-height:1.35;box-shadow:0 1px 3px rgba(0,0,0,0.4);">
        <div style="display:flex;align-items:center;gap:6px;${aboard > 0 ? 'margin-bottom:2px;padding-bottom:2px;border-bottom:1px solid rgba(15,23,42,0.25);' : ''}">
          🚛 ${primary}${placeName ? ` <span style="opacity:0.75;font-weight:500">📍 ${placeName}</span>` : ''}
          ${mph > 0 ? `<span class="truck-mph" style="margin-left:auto;background:#0f172a;color:#fff;padding:1px 5px;border-radius:3px;font-size:11px;font-weight:600;">${mph} mph</span>` : ''}
        </div>
        ${lines}
      </div>`;
  }
  // When fanned, the leader line already ends in a dot at the truck's true GPS — a
  // second dot on the label looks like two markers, so show just the name.
  const dot = fanned ? '' : `<span style="width:16px;height:16px;border-radius:50%;background:${STATUS_COLOR[v.status]};border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.4);flex-shrink:0;"></span>`;
  return `
    ${dot}
    <span style="background:rgba(15,23,42,0.9);color:#fff;padding:3px 7px;border-radius:4px;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.4);">${primary}${sub ? ` <span style="opacity:0.7;font-weight:500">${sub}</span>` : ''}</span>`;
}
const HAULER_ICON_ZOOM = 14;  // at/above this zoom, haulers shrink to a small dot

export default function TrucksLowboys() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);   // "updating positions" pill after the app resumes
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'list' | 'map'>('list');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeJobs, setActiveJobs] = useState<JobGeofence[]>([]);
  // Bumped whenever Mapbox (re)loads its style — e.g. after a mobile WebGL
  // context loss wipes all custom sources/layers — so the geofence data gets
  // re-pushed into the freshly re-added sources (matches FleetView).
  const [styleEpoch, setStyleEpoch] = useState(0);
  const [employees, setEmployees] = useState<{ employee_id: number; first_name: string; last_name: string }[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [cameras, setCameras] = useState<{ url: string; input?: string; startTime?: string; capturedAtTime?: string }[] | null>(null);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [lightbox, setLightbox] = useState<{ photos: string[]; index: number; captions?: (string | null)[] } | null>(null);
  const [popup, setPopup] = useState<string | null>(null);   // tap-to-dismiss notice (e.g. no camera footage)
  const [liveShareLoading, setLiveShareLoading] = useState(false);
  const [tripDate, setTripDate] = useState<string>(todayEstYmd());
  const [trips, setTrips] = useState<TripData | null>(null);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [selectedTripIndex, setSelectedTripIndex] = useState<number | null>(null);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  const [runActive, setRunActive] = useState(false);          // "Show current run" trail is showing
  const [runFrom, setRunFrom] = useState<string | null>(null); // departure place name for the bar label
  const [runLoading, setRunLoading] = useState(false);
  const auth = useContext(AuthContext);
  const isSuperAdmin = auth?.user?.is_super_admin === true;

  const selected = useMemo(() => vehicles.find(v => v.equipment_id === selectedId) || null, [vehicles, selectedId]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [res, jobsRes] = await Promise.all([api('/fleet-vehicles'), api('/jobs')]);
      if (res?.success) { setVehicles(res.data.vehicles); setTotals(res.data.totals); }
      else setError(res?.error || 'Failed to load trucks');
      const jobs = ((jobsRes?.data || []) as any[]).filter(j => j.is_active && j.lat != null && j.lng != null).map(j => ({
        job_id: j.job_id, job_name: j.job_name, lat: Number(j.lat), lng: Number(j.lng), geofence_type: j.geofence_type,
        geofence_data: typeof j.geofence_data === 'string' ? (() => { try { return JSON.parse(j.geofence_data); } catch { return null; } })() : j.geofence_data,
      }));
      setActiveJobs(jobs);
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally { setLoading(false); setResuming(false); }
  };
  useEffect(() => { load(); }, []);

  // Log a "trucks" page visit so the super-admin activity view counts PWA opens
  // too — same DASHBOARD_VISITS key as bfav5's /trucks, so the two producers
  // aggregate. Tagged source=pwa. Fire-and-forget (analytics only).
  useEffect(() => {
    fetchWithTimeout(`${API_BASE_URL}/api/admin/dashboard/visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('bfa_token')}`,
      },
      body: JSON.stringify({ page: 'trucks', source: 'pwa' }),
    }).catch(() => { /* analytics-only; don't bother the user */ });
  }, []);
  useEffect(() => {
    api('/employees').then(r => {
      const list = (r?.data || []) as any[];
      setEmployees(list.filter(e => e.is_active !== false).map(e => ({ employee_id: e.employee_id, first_name: e.first_name, last_name: e.last_name }))
        .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')));
    }).catch(() => {});
  }, []);

  // Fast position poll (Samsara has no push). positions=1 = one cheap call.
  const pollPositions = async () => {
    try {
      const res = await api('/fleet-vehicles?positions=1');
      if (!res?.success) return;
      const byId = new Map<number, Vehicle>((res.data.vehicles as Vehicle[]).map(v => [v.equipment_id, v]));
      setVehicles(prev => prev.map(v => {
        const p = byId.get(v.equipment_id);
        if (!p) return v;
        // Never move a marker BACKWARD to an older fix — the 10s poll can return a
        // snapshot older than the latest SSE push (Traccar), which lerped the
        // marker back then forward (the 50ft bounce). Skip if this fix is staler.
        const pT = p.synced_at ? new Date(p.synced_at).getTime() : 0;
        const vT = v.synced_at ? new Date(v.synced_at).getTime() : 0;
        if (pT && vT && pT < vT) return v;
        return { ...v, lat: p.lat, lng: p.lng, speed_mph: p.speed_mph, heading: p.heading, ignition_on: p.ignition_on, address: p.address ?? v.address, synced_at: p.synced_at, status: p.status, position_source: p.position_source, current_job: p.current_job, current_place: p.current_place, parked_minutes: p.parked_minutes };
      }));
      setTotals(t => t ? { ...t, count: res.data.totals.count, moving: res.data.totals.moving, idle: res.data.totals.idle, parked: res.data.totals.parked, offline: res.data.totals.offline } : res.data.totals);
    } catch { /* next tick */ }
  };
  useEffect(() => {
    const fast = setInterval(() => { if (document.visibilityState !== 'hidden') pollPositions(); }, 10000);
    const full = setInterval(() => { if (document.visibilityState !== 'hidden') load(true); }, 60000);
    // On resume (focus / visibility / bfcache restore): refresh, and if a truck is
    // focused, resize + recenter on it. The map canvas can resize/lose context on
    // reopen, leaving the focused truck off-screen ("you see it then it disappears").
    const onResume = () => {
      if (document.visibilityState === 'hidden') return;
      snapNextRef.current = true;   // jump markers to the fresh fix (no glide-forward flash)
      setResuming(true);            // show "updating positions" until the fresh fetch lands
      load(true);
      // Only recenter when we're actively following the live truck — not while a
      // trip route is framed (followIdRef is null then), so history stays put.
      const id = followIdRef.current;
      if (id != null) {
        setTimeout(() => {
          const map = mapRef.current; if (!map) return;
          map.resize();
          const ent = markersRef.current.get(id);
          if (ent && Number.isFinite(ent.lng) && Number.isFinite(ent.lat)) {
            map.easeTo({ center: [ent.lng, ent.lat], zoom: Math.max(map.getZoom(), 14), duration: 400 });
          }
        }, 350);
      }
    };
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      clearInterval(fast); clearInterval(full);
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time updates for Traccar-tracked trucks via the shared fleet SSE stream.
  useEffect(() => {
    const token = localStorage.getItem('bfa_token');
    if (!token) return;
    const es = new EventSource(`${API_BASE_URL}/api/fleet/stream?token=${encodeURIComponent(token)}`);
    es.addEventListener('message', (e) => {
      try {
        const u = JSON.parse((e as MessageEvent).data);
        if (u?.type !== 'position' || !u.equipment_id) return;
        setVehicles(prev => prev.map(v => {
          if (v.equipment_id !== u.equipment_id) return v;
          // Don't move the marker backward to an older fix (out-of-order push).
          const uT = u.synced_at ? new Date(u.synced_at).getTime() : 0;
          const vT = v.synced_at ? new Date(v.synced_at).getTime() : 0;
          if (uT && vT && uT < vT) return v;
          const speed_mph = u.speed_kph != null ? Math.round(u.speed_kph * 0.621371) : v.speed_mph;
          const status: Vehicle['status'] = (speed_mph != null && speed_mph > 3) ? 'moving' : 'parked';
          return { ...v, lat: u.lat ?? v.lat, lng: u.lng ?? v.lng, speed_mph, ignition_on: !!u.ignition_on, synced_at: u.synced_at ?? v.synced_at, status, position_source: 'traccar' };
        }));
      } catch { /* ignore */ }
    });
    return () => es.close();
  }, []);

  // Always-current vehicles, so a fly issued right after resume reads the FRESH
  // position rather than the (possibly stale) click-time render closure.
  const vehiclesRef = useRef<Vehicle[]>(vehicles); vehiclesRef.current = vehicles;

  // ── Drawer actions ─────────────────────────────────────────────────────────
  const openDrawer = (id: number) => {
    setSelectedId(id); setCameras(null); setTrips(null); setSelectedTripIndex(null);
    hideCurrentRun();   // clear any run trail from the previously-selected truck
    followIdRef.current = id;   // follow this truck as it moves
    // Selecting a truck jumps to the map zoomed in on it, with the detail card
    // collapsed (tap the collapsed bar to expand).
    setSheetCollapsed(true);
    setTab('map');
    // rAF-wait until the map is actually sized (display:none→block needs a layout
    // pass), then resize + fly to the FRESHEST position. Reading the closure's
    // `vehicles` at click time centered on the OLD spot right after a resume;
    // reading vehiclesRef (then the live marker) at fly time fixes that, and
    // follow-cam keeps it centered as new fixes arrive.
    let tries = 0;
    const fly = () => {
      const map = mapRef.current, c = mapContainer.current;
      if (map && c && c.offsetWidth > 20 && c.offsetHeight > 20) {
        map.resize();
        const cur = vehiclesRef.current.find(x => x.equipment_id === id);
        const ent = markersRef.current.get(id);
        // Prefer the marker's smoothed (rendered) spot so the playback loop's
        // follow-cam doesn't yank the camera back after the flyTo.
        const lng = ent?.lng ?? cur?.lng ?? null;
        const lat = ent?.lat ?? cur?.lat ?? null;
        if (lng != null && lat != null) { suppressFollowUntilRef.current = performance.now() + 900; map.flyTo({ center: [lng, lat], zoom: 14, duration: 600, essential: true }); }
      } else if (tries++ < 40) {
        requestAnimationFrame(fly);
      }
    };
    requestAnimationFrame(fly);
  };
  const closeDrawer = () => { setSelectedId(null); followIdRef.current = null; clearRoute(); hideCurrentRun(); };

  // Back button (header chevron / native) closes the open layer instead of
  // leaving the page: drawer first, then Map→List, else navigate away. A single
  // re-armed history sentinel drives it (refs read the latest state).
  const selRef = useRef<number | null>(selectedId); selRef.current = selectedId;
  const runActiveRef = useRef(false); runActiveRef.current = runActive;   // for the rAF loop (closure is stale)
  const tabRef = useRef<'list' | 'map'>(tab); tabRef.current = tab;
  const navigate = useNavigate();
  useEffect(() => {
    window.history.pushState({ tlGuard: true }, '');
    const onPop = () => {
      if (selRef.current != null) { closeDrawer(); window.history.pushState({ tlGuard: true }, ''); }
      else if (tabRef.current === 'map') { setTab('list'); window.history.pushState({ tlGuard: true }, ''); }
      // Nothing open: go to the dashboard explicitly. history.back() here can walk
      // off the end of the stack and exit the app (PWA cold-launched on this route,
      // or a back/forward race consumed the sentinel) — navigate('/') never does.
      else { navigate('/'); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCameras = async (v: Vehicle) => {
    if (!v.samsara_vehicle_id) return;
    setCamerasLoading(true); setCameras(null);
    try {
      const res = await api(`/trucker-location/vehicle/${v.samsara_vehicle_id}/all-cameras`);
      setCameras(res?.data?.cameras || []);
    } catch (e: any) { setError(e.message); setCameras([]); }
    finally { setCamerasLoading(false); }
  };
  // Load cameras and open them straight in the fullscreen viewer (used by the
  // collapsed map bottom-bar so you don't have to expand the detail drawer).
  const openCameras = async (v: Vehicle) => {
    if (!v.samsara_vehicle_id) return;
    setCamerasLoading(true);
    try {
      const res = await api(`/trucker-location/vehicle/${v.samsara_vehicle_id}/all-cameras`);
      const cams = res?.data?.cameras || [];
      setCameras(cams);
      const withUrl = cams.filter((c: any) => c.url);
      if (withUrl.length > 0) setLightbox({ photos: withUrl.map((c: any) => c.url), index: 0, captions: withUrl.map(photoCaption) });
      else setPopup('No live camera footage available right now.');
    } catch (e: any) { setPopup(e.message || 'Could not load cameras.'); }
    finally { setCamerasLoading(false); }
  };
  const loadLiveShare = async (v: Vehicle) => {
    if (!v.samsara_vehicle_id) return;
    setLiveShareLoading(true);
    try {
      const res = await api(`/samsara/live-share/${v.samsara_vehicle_id}`);
      if (res?.url) window.open(res.url, '_blank');
      else setError(res?.error || 'No live-share link returned');
    } catch (e: any) { setError(e.message); }
    finally { setLiveShareLoading(false); }
  };
  const assignTruck = async (v: Vehicle, employeeId: number | null) => {
    setAssignSaving(true);
    try {
      await api(`/fleet-vehicles/${v.equipment_id}/assign`, { method: 'PUT', body: JSON.stringify({ employee_id: employeeId }) });
      const emp = employeeId != null ? employees.find(e => e.employee_id === employeeId) : null;
      const assigned_to = emp ? { employee_id: emp.employee_id, first_name: emp.first_name, name: `${emp.first_name} ${emp.last_name}`.trim() } : null;
      setVehicles(prev => prev.map(x => x.equipment_id === v.equipment_id ? { ...x, assigned_to } : x));
    } catch (e: any) { setError(e.message); }
    finally { setAssignSaving(false); }
  };
  const loadTrips = async (v: Vehicle) => {
    followIdRef.current = null;   // stop following the live truck — we're viewing history, keep the whole route framed
    hideCurrentRun();             // the manual trip browser replaces the live run trail
    setTripsLoading(true); setTrips(null); clearRoute();
    try {
      const res = await api(`/fleet-vehicles/${v.equipment_id}/trips?date=${tripDate}`);
      const data: TripData = res.data;
      setTrips(data); setSelectedTripIndex(null); setTab('map'); renderRoute(data, null);
    } catch (e: any) { setError(e.message); }
    finally { setTripsLoading(false); }
  };

  // ── Map ──────────────────────────────────────────────────────────────────
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const collapsedBarRef = useRef<HTMLDivElement | null>(null);   // measured so the trip route fits ABOVE the bottom bar
  // "Show current run": live breadcrumb from where the selected truck DEPARTED
  // (its last stop/job) to its live position, drawn on a dedicated layer.
  const liveTrailRef = useRef<[number, number][]>([]);
  const trailStartMarkerRef = useRef<mapboxgl.Marker | null>(null);
  // Option 2 smooth playback: each marker keeps a short queue of recent TRUE-GPS
  // fixes keyed by real fix time (synced_at), and is rendered ~PLAYBACK_DELAY_MS
  // behind live, interpolating between the two fixes that bracket the playback
  // clock. Spread/fan offset is stored separately and applied live.
  interface FixPt { t: number; lng: number; lat: number }   // t = GPS fix time (epoch ms)
  interface MEntry { marker: mapboxgl.Marker; el: HTMLDivElement; lng: number; lat: number; sig: string; labelLeft: boolean; srcLng: number; srcLat: number; buf: FixPt[]; offLng: number; offLat: number; clockFixT?: number; clockArr?: number }
  const markersRef = useRef<Map<number, MEntry>>(new Map());
  const playRafRef = useRef<number | null>(null);
  const suppressFollowUntilRef = useRef(0);   // pause follow-cam centering during a programmatic flyTo
  const lastFollowAtRef = useRef(0);
  const lastTrailDrawRef = useRef(0);   // throttle run-trail redraws
  const stopMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const didFitRef = useRef(false);
  const followIdRef = useRef<number | null>(null);  // selected truck the camera follows
  const snapNextRef = useRef(false);                 // jump markers (no glide) on the next render — set on resume
  const [zoomTick, setZoomTick] = useState(0);       // re-spread markers on zoom change

  // Feed a TRUE-GPS fix into a marker's playback queue, keyed by real fix time.
  const pushFix = (entry: MEntry, lng: number, lat: number, fixT: number) => {
    const last = entry.buf[entry.buf.length - 1];
    if (last && fixT <= last.t) return;   // stale / duplicate fix — ignore
    if (!last || fixT - last.t > 30000) entry.buf = [{ t: fixT, lng, lat }];   // first move / resume after a stop → fresh
    else { entry.buf.push({ t: fixT, lng, lat }); const cut = fixT - PLAYBACK_DELAY_MS - 4000; while (entry.buf.length > 2 && entry.buf[1].t < cut) entry.buf.shift(); }
    entry.clockFixT = fixT; entry.clockArr = performance.now();
  };
  const snapFix = (entry: MEntry, lng: number, lat: number, fixT: number) => {
    entry.buf = [{ t: fixT, lng, lat }];
    entry.clockFixT = fixT; entry.clockArr = performance.now();
  };

  // One rAF for the whole map: render each marker at its playback clock by
  // interpolating buffered fixes, then add the live spread offset. Keep the
  // followed truck centered on its smoothed marker (throttled easeTo, not
  // per-frame, which jitters).
  useEffect(() => {
    const tick = () => {
      const map = mapRef.current;
      if (map) {
        const nowMs = performance.now();
        for (const entry of markersRef.current.values()) {
          const b = entry.buf;
          if (b.length === 0 || entry.clockFixT == null || entry.clockArr == null) continue;
          const renderT = entry.clockFixT + (nowMs - entry.clockArr) - PLAYBACK_DELAY_MS;
          let tlng: number, tlat: number;
          if (renderT <= b[0].t) { tlng = b[0].lng; tlat = b[0].lat; }
          else if (renderT >= b[b.length - 1].t) { const p = b[b.length - 1]; tlng = p.lng; tlat = p.lat; }
          else { let i = 0; while (i < b.length - 1 && b[i + 1].t <= renderT) i++; const a = b[i], c = b[i + 1]; const f = c.t === a.t ? 1 : (renderT - a.t) / (c.t - a.t); tlng = a.lng + (c.lng - a.lng) * f; tlat = a.lat + (c.lat - a.lat) * f; }
          const lng = tlng + entry.offLng, lat = tlat + entry.offLat;
          if (Math.abs(lng - entry.lng) > 1e-9 || Math.abs(lat - entry.lat) > 1e-9) { entry.marker.setLngLat([lng, lat]); entry.lng = lng; entry.lat = lat; }
        }
        const fid = followIdRef.current;
        if (fid != null && nowMs >= suppressFollowUntilRef.current && nowMs - lastFollowAtRef.current > 900) {
          const fe = markersRef.current.get(fid);
          if (fe) {
            const c = map.getCenter();
            if (Math.abs(c.lng - fe.lng) > 1e-5 || Math.abs(c.lat - fe.lat) > 1e-5) map.easeTo({ center: [fe.lng, fe.lat], duration: 1000 });
            lastFollowAtRef.current = nowMs;
          }
        }
        // "Show current run": extend the trail to the SMOOTHED marker position
        // (not the raw live fix), so the line ends exactly at the truck instead of
        // running ~one playback-delay ahead of it.
        if (runActiveRef.current && selRef.current != null) {
          const re = markersRef.current.get(selRef.current);
          if (re) {
            const trail = liveTrailRef.current;
            const last = trail[trail.length - 1];
            const dx = last ? re.lng - last[0] : 1, dy = last ? re.lat - last[1] : 1;
            if (!last || dx * dx + dy * dy > 6e-9) {   // moved ~8m → add a point
              trail.push([re.lng, re.lat]);
              if (trail.length > 3000) trail.shift();
              if (nowMs - lastTrailDrawRef.current > 120) {   // ~8fps redraw
                lastTrailDrawRef.current = nowMs;
                const src = map.getSource('ttrail') as mapboxgl.GeoJSONSource | undefined;
                if (src) src.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: trail }, properties: {} });
              }
            }
          }
        }
      }
      playRafRef.current = requestAnimationFrame(tick);
    };
    playRafRef.current = requestAnimationFrame(tick);
    return () => { if (playRafRef.current != null) cancelAnimationFrame(playRafRef.current); };
  }, []);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new mapboxgl.Map({ container: mapContainer.current, style: 'mapbox://styles/mapbox/streets-v12', center: [-81.7, 26.2], zoom: 9 });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    // Re-spread markers + swap hauler icon size when the zoom changes.
    map.on('zoomend', () => setZoomTick(t => t + 1));
    // A user-driven pan/zoom releases follow-cam (programmatic moves carry no originalEvent).
    map.on('movestart', (e: any) => { if (e.originalEvent) followIdRef.current = null; });
    // Runs on the initial 'load' AND every 'style.load' — a style reload
    // (notably after a mobile WebGL context loss) wipes every custom
    // source/layer, and 'load' never refires, so without the 'style.load'
    // hook the geofences vanished until the page was remounted.
    const ensureLayers = () => {
      if (map.getSource('tjobs')) return;
      const empty = { type: 'FeatureCollection' as const, features: [] };
      map.addSource('tjobs', { type: 'geojson', data: empty });
      map.addLayer({ id: 'tjobs-fill', type: 'fill', source: 'tjobs', paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.08 } });
      map.addLayer({ id: 'tjobs-line', type: 'line', source: 'tjobs', paint: { 'line-color': '#f59e0b', 'line-width': 1.5, 'line-opacity': 0.5 } });
      map.addSource('tjob-labels', { type: 'geojson', data: empty });
      map.addLayer({ id: 'tjob-labels-sym', type: 'symbol', source: 'tjob-labels', layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-offset': [0, 0.6], 'text-anchor': 'top' }, paint: { 'text-color': '#b45309', 'text-halo-color': '#fff', 'text-halo-width': 1.2 } });
      map.addSource('troute', { type: 'geojson', data: empty });
      map.addLayer({ id: 'troute-line', type: 'line', source: 'troute', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': '#ea580c', 'line-width': 4, 'line-opacity': 0.85 } });
      // Live "current run" breadcrumb (departure → live position).
      map.addSource('ttrail', { type: 'geojson', data: empty });
      map.addLayer({ id: 'ttrail-line', type: 'line', source: 'ttrail', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': '#2563eb', 'line-width': 4, 'line-opacity': 0.9 } });
      // Leader lines + true-position dots for trucks fanned out by spreadPositions.
      map.addSource('tspread', { type: 'geojson', data: empty });
      map.addLayer({ id: 'tspread-lines', type: 'line', source: 'tspread', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#64748b', 'line-width': 1.2, 'line-opacity': 0.7 } });
      map.addLayer({ id: 'tspread-dots', type: 'circle', source: 'tspread', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 3, 'circle-color': '#64748b', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } });
      // Fresh sources are empty — bump the epoch so the geofence effect
      // re-pushes activeJobs into them.
      setStyleEpoch(e => e + 1);
    };
    map.on('load', ensureLayers);
    map.on('style.load', ensureLayers);
    mapRef.current = map;
    // Without this every visit leaked a live WebGL context; once the browser's
    // context cap (~8 on iOS Safari) filled up it force-lost the oldest map's
    // context, wiping custom layers on whatever map was on screen.
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      const s1 = map.getSource('tjobs') as mapboxgl.GeoJSONSource | undefined;
      const s2 = map.getSource('tjob-labels') as mapboxgl.GeoJSONSource | undefined;
      if (!s1 || !s2) { setTimeout(apply, 200); return; }
      const { polys, labels } = buildJobFCs(activeJobs);
      s1.setData(polys); s2.setData(labels);
    };
    apply();
    return () => { cancelled = true; };
  }, [activeJobs, styleEpoch]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const zoom = map.getZoom();
    const zoomedIn = zoom >= HAULER_ICON_ZOOM;
    const spread = zoom >= SPREAD_MIN_ZOOM ? spreadPositions(vehicles, zoom) : new Map<number, { lng: number; lat: number; labelLeft: boolean }>();   // fan stacked trucks only when zoomed in
    // Samsara-style declutter: collapse a marker to a bare dot when its name label
    // would collide with another (only when all trucks are shown). Pixel distance
    // between projected points is pan-invariant, so this only changes on zoom.
    const crowded = new Set<number>();
    if (selectedId == null) {
      const COLLIDE_PX = 20;   // only collapse to a dot when labels really overlap — keep names visible at lower zoom
      const pts: Array<[number, number, number]> = [];
      for (const v of vehicles) {
        if (v.lat == null || v.lng == null) continue;
        const sp = spread.get(v.equipment_id);
        const dl = sp?.lng ?? (v.lng as number), da = sp?.lat ?? (v.lat as number);
        const p = map.project([dl, da]);
        pts.push([v.equipment_id, p.x, p.y]);
      }
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i][1] - pts[j][1], dy = pts[i][2] - pts[j][2];
        if (dx * dx + dy * dy < COLLIDE_PX * COLLIDE_PX) { crowded.add(pts[i][0]); crowded.add(pts[j][0]); }
      }
    }
    const live = new Set<number>();
    const bounds = new mapboxgl.LngLatBounds();
    const leader: any[] = [];   // leader lines + true-pos dots for fanned trucks
    for (const v of vehicles) {
      if (v.lat == null || v.lng == null) continue;
      live.add(v.equipment_id);
      bounds.extend([v.lng, v.lat]);
      // The selected truck is the only one shown, so don't fan it — render at its
      // TRUE GPS so the camera (which centers on true GPS) lands on it, in view.
      const isSel = selectedId === v.equipment_id;
      const sp = isSel ? undefined : spread.get(v.equipment_id);
      const dLng = sp?.lng ?? (v.lng as number), dLat = sp?.lat ?? (v.lat as number);
      const labelLeft = sp?.labelLeft ?? false;
      const visible = selectedId == null || isSel;
      const fanned = visible && !isSel && (Math.abs(dLng - v.lng) > 1e-9 || Math.abs(dLat - v.lat) > 1e-9);
      if (fanned) {
        leader.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [[dLng, dLat], [v.lng, v.lat]] }, properties: {} });
        leader.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [v.lng, v.lat] }, properties: {} });
      }
      const dotOnly = crowded.has(v.equipment_id);
      const sig = markerSig(v, zoomedIn, dotOnly, fanned);
      let entry = markersRef.current.get(v.equipment_id);
      // A marker's anchor/flex-direction can't change after creation, so rebuild it
      // when a truck flips to the other half of the ring.
      if (entry && entry.labelLeft !== labelLeft) {
        entry.marker.remove(); markersRef.current.delete(v.equipment_id); entry = undefined;
      }
      if (entry) {
        const trulyMoved = Math.abs((v.lng as number) - entry.srcLng) > 1e-9 || Math.abs((v.lat as number) - entry.srcLat) > 1e-9;
        // Spread/fan offset is applied LIVE by the playback loop, so a zoom
        // re-spread (or an unrelated truck's tick) just updates the offset — it
        // never disturbs this marker's in-flight smoothing.
        entry.offLng = dLng - (v.lng as number); entry.offLat = dLat - (v.lat as number);
        const fixT = v.synced_at ? new Date(v.synced_at).getTime() : Date.now();
        if (snapNextRef.current) {
          snapFix(entry, v.lng as number, v.lat as number, fixT);
          entry.lng = dLng; entry.lat = dLat; entry.marker.setLngLat([dLng, dLat]);
        } else if (trulyMoved) {
          pushFix(entry, v.lng as number, v.lat as number, fixT);   // queue by fix time; loop interpolates to it
        }
        entry.srcLng = v.lng as number; entry.srcLat = v.lat as number;
        if (entry.sig !== sig) { entry.el.innerHTML = markerHTML(v, zoomedIn, dotOnly, fanned); entry.sig = sig; }
        // Live-update the hauler speed badge in place (no DOM rebuild → no flash).
        if (v.is_hauler) { const sp = entry.el.querySelector('.truck-mph') as HTMLElement | null; if (sp) sp.textContent = `${v.speed_mph ?? 0} mph`; }
        entry.el.style.display = visible ? 'flex' : 'none';
      } else {
        const el = document.createElement('div');
        // West-half trucks: label hangs LEFT of the dot (row-reverse + anchor right)
        // so names point outward from the cluster, never across its center.
        el.style.cssText = `display:${visible ? 'flex' : 'none'};align-items:center;gap:6px;cursor:pointer;${labelLeft ? 'flex-direction:row-reverse;' : ''}`;
        el.innerHTML = markerHTML(v, zoomedIn, dotOnly, fanned);
        el.addEventListener('click', () => openDrawer(v.equipment_id));
        const marker = new mapboxgl.Marker({ element: el, anchor: labelLeft ? 'right' : 'left' }).setLngLat([dLng, dLat]).addTo(map);
        const fixT = v.synced_at ? new Date(v.synced_at).getTime() : Date.now();
        markersRef.current.set(v.equipment_id, { marker, el, lng: dLng, lat: dLat, sig, labelLeft, srcLng: v.lng as number, srcLat: v.lat as number, buf: [{ t: fixT, lng: v.lng as number, lat: v.lat as number }], offLng: dLng - (v.lng as number), offLat: dLat - (v.lat as number), clockFixT: fixT, clockArr: performance.now() });
      }
    }
    for (const [id, entry] of markersRef.current.entries()) {
      if (!live.has(id)) { entry.marker.remove(); markersRef.current.delete(id); }
    }
    snapNextRef.current = false;   // consumed — subsequent updates glide normally
    const spreadSrc = map.getSource('tspread') as mapboxgl.GeoJSONSource | undefined;
    if (spreadSrc) spreadSrc.setData({ type: 'FeatureCollection', features: leader });
    if (!didFitRef.current && !bounds.isEmpty()) { didFitRef.current = true; map.fitBounds(bounds, { padding: 60, maxZoom: 13 }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, selectedId, zoomTick]);

  // (Follow-cam centering lives in the playback loop above — it keeps the selected
  // truck centered on its *smoothed* marker, not the raw latest fix.)

  const clearRoute = () => {
    const map = mapRef.current;
    if (map?.getSource('troute')) (map.getSource('troute') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
    stopMarkersRef.current.forEach(m => m.remove()); stopMarkersRef.current = [];
    setSelectedTripIndex(null);
  };
  const renderRoute = (data: TripData, selIdx: number | null) => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource('troute') as mapboxgl.GeoJSONSource | undefined;
      if (!src) { setTimeout(apply, 200); return; }
      const shown = data.trips.map((t, i) => ({ t, i })).filter(x => selIdx == null || x.i === selIdx);
      src.setData({ type: 'FeatureCollection', features: shown.map(({ t }) => ({ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: t.path } })) });
      stopMarkersRef.current.forEach(m => m.remove()); stopMarkersRef.current = [];
      if (selIdx == null) {
        data.stops.forEach((s, i) => {
          const el = document.createElement('div');
          el.style.cssText = 'width:22px;height:22px;border-radius:50%;background:#0f172a;color:#fff;border:2px solid #ea580c;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;';
          el.textContent = String(i + 1);
          el.title = `${s.job?.job_name || s.place?.name || s.address || 'Stop'} · ${formatDur(s.duration_min)}`;
          stopMarkersRef.current.push(new mapboxgl.Marker({ element: el }).setLngLat([s.lng, s.lat]).addTo(map));
        });
      }
      // Beginning + ending markers for the shown route (start = first fix, end = last).
      if (shown.length > 0) {
        const fp = shown[0].t.path, lp = shown[shown.length - 1].t.path;
        if (fp?.length && lp?.length) {
          const startEl = document.createElement('div');
          startEl.title = 'Trip start';
          startEl.innerHTML = '<div style="width:16px;height:16px;border-radius:50%;background:#16a34a;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);"></div>';
          const endEl = document.createElement('div');
          endEl.title = 'Trip end';
          endEl.innerHTML = '<div style="width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #dc2626;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 1px 4px rgba(0,0,0,.45);">🏁</div>';
          stopMarkersRef.current.push(new mapboxgl.Marker({ element: startEl }).setLngLat(fp[0] as [number, number]).addTo(map));
          stopMarkersRef.current.push(new mapboxgl.Marker({ element: endEl }).setLngLat(lp[lp.length - 1] as [number, number]).addTo(map));
        }
      }
      const b = new mapboxgl.LngLatBounds();
      shown.forEach(({ t }) => t.path.forEach(c => b.extend(c)));
      if (selIdx == null) data.stops.forEach(s => b.extend([s.lng, s.lat]));
      setTimeout(() => {
        map.resize();
        if (b.isEmpty()) return;
        // Fit the route into the area ABOVE the collapsed bottom bar (+ nav): pad
        // the bottom by however much the bar overlaps the map container.
        const c = mapContainer.current, barEl = collapsedBarRef.current;
        let bottomPad = 60;
        if (c && barEl) {
          const overlap = c.getBoundingClientRect().bottom - barEl.getBoundingClientRect().top;
          bottomPad = Math.max(50, Math.min(overlap + 16, c.offsetHeight * 0.55));
        }
        map.fitBounds(b, { padding: { top: 70, bottom: bottomPad, left: 40, right: 40 }, maxZoom: selIdx == null ? 15 : 16 });
      }, 120);
    };
    apply();
  };
  const selectTrip = (i: number | null) => {
    followIdRef.current = null;   // viewing a trip — don't let the live follow-cam recenter on the truck
    setSelectedTripIndex(i);
    setTab('map');
    if (trips) renderRoute(trips, i);
    // Collapse the sheet when isolating one trip so the route is actually visible.
    if (i != null) setSheetCollapsed(true);
  };
  // Step trips from the collapsed bar (wrap-around) so you can flip between them
  // without expanding the full card over the map.
  const stepTrip = (dir: 1 | -1) => {
    const n = trips?.trips.length ?? 0;
    if (!n) return;
    const cur = selectedTripIndex ?? (dir === 1 ? -1 : 0);
    selectTrip((cur + dir + n) % n);
  };
  // Exit trip history → clear the drawn route, hide the trip browser, and resume
  // following the truck's live position.
  const backToLive = () => {
    clearRoute();
    setTrips(null);
    setSelectedTripIndex(null);
    hideCurrentRun();
    if (selectedId == null) return;
    followIdRef.current = selectedId;
    const v = vehiclesRef.current.find(x => x.equipment_id === selectedId);
    const ent = markersRef.current.get(selectedId);
    const lng = ent?.lng ?? v?.lng ?? null, lat = ent?.lat ?? v?.lat ?? null;
    if (lng != null && lat != null && mapRef.current) {
      suppressFollowUntilRef.current = performance.now() + 900;
      mapRef.current.flyTo({ center: [lng, lat], zoom: 15, duration: 600, essential: true });
    }
  };

  // ── "Show current run": departure → live breadcrumb ────────────────────────
  const hideCurrentRun = () => {
    setRunActive(false); setRunFrom(null);
    liveTrailRef.current = [];
    const map = mapRef.current;
    if (map?.getSource('ttrail')) (map.getSource('ttrail') as mapboxgl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
    if (trailStartMarkerRef.current) { trailStartMarkerRef.current.remove(); trailStartMarkerRef.current = null; }
  };
  const showCurrentRun = async () => {
    if (!selected) return;
    setRunLoading(true);
    try {
      const res = await api(`/fleet-vehicles/${selected.equipment_id}/trips?date=${todayEstYmd()}`);
      const data: TripData = res.data;
      const last = data.trips[data.trips.length - 1];
      if (!last || !last.path || last.path.length === 0) { setError('No current run for this truck today.'); return; }
      // Trim the departure→now path to the marker's SMOOTHED position so the line
      // starts ending at the truck (the marker lags the latest fix by the delay).
      const re0 = markersRef.current.get(selected.equipment_id);
      const mLng = re0?.lng ?? selected.lng, mLat = re0?.lat ?? selected.lat;
      let path = last.path.map(c => [c[0], c[1]] as [number, number]);
      if (mLng != null && mLat != null && path.length) {
        let bi = path.length - 1, bd = Infinity;
        for (let i = 0; i < path.length; i++) { const dx = path[i][0] - mLng, dy = path[i][1] - mLat; const d = dx * dx + dy * dy; if (d < bd) { bd = d; bi = i; } }
        path = path.slice(0, bi + 1);
        path.push([mLng, mLat]);
      }
      liveTrailRef.current = path;
      setRunFrom(last.from ?? null);
      setRunActive(true);
      setTrips(null); setSelectedTripIndex(null);   // run is independent of the manual trip browser
      setTab('map'); setSheetCollapsed(true);
      followIdRef.current = selected.equipment_id;   // keep following the live truck
      const map = mapRef.current;
      if (!map) return;
      const src = map.getSource('ttrail') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: liveTrailRef.current }, properties: {} });
      if (trailStartMarkerRef.current) trailStartMarkerRef.current.remove();
      const el = document.createElement('div');
      el.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;"><div style="background:#2563eb;color:#fff;font-size:11px;font-weight:700;padding:2px 7px;border-radius:6px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.4);max-width:150px;overflow:hidden;text-overflow:ellipsis;">Left ${last.from || 'here'}</div><div style="width:14px;height:14px;border-radius:50%;background:#fff;border:3px solid #2563eb;margin-top:2px;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div></div>`;
      trailStartMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat(liveTrailRef.current[0]).addTo(map);
      const b = new mapboxgl.LngLatBounds();
      liveTrailRef.current.forEach(c => b.extend(c));
      if (selected.lng != null && selected.lat != null) b.extend([selected.lng, selected.lat]);
      const c2 = mapContainer.current, barEl = collapsedBarRef.current;
      let bottomPad = 60;
      if (c2 && barEl) { const overlap = c2.getBoundingClientRect().bottom - barEl.getBoundingClientRect().top; bottomPad = Math.max(50, Math.min(overlap + 16, c2.offsetHeight * 0.55)); }
      setTimeout(() => { map.resize(); if (!b.isEmpty()) map.fitBounds(b, { padding: { top: 70, bottom: bottomPad, left: 40, right: 40 }, maxZoom: 15 }); }, 140);
    } catch (e: any) { setError(e.message); }
    finally { setRunLoading(false); }
  };
  // (Trail growth is handled in the playback loop — it extends to the SMOOTHED
  // marker position so the line ends at the truck, not ahead of it.)

  useEffect(() => { if (tab === 'map') setTimeout(() => mapRef.current?.resize(), 80); }, [tab]);

  // ── List grouping ──────────────────────────────────────────────────────────
  const isHeavy = (v: Vehicle) => v.is_hauler || v.has_camera;
  // Samsara-only trucks (e.g. Pete 589) don't write engine-hours history — that's
  // Traccar-only — so ran_today_minutes is null for them and they could never
  // reach the "Active Today" card. Surface them there when they're currently in
  // use (moving or idling), which is the only "is it working" signal we have.
  const inUseNow = (v: Vehicle) => v.status === 'moving' || v.status === 'idle';
  const ranToday = (v: Vehicle) =>
    (v.ran_today_minutes ?? 0) > 0 || (v.ran_today_minutes == null && inUseNow(v));
  const allHeavy = vehicles.filter(isHeavy);
  // Heavy haulers that RAN today float into their own "Active Today" card on top
  // (most-used first); the rest stay in the regular Haulers section below.
  const activeHaulers = allHeavy.filter(ranToday).sort((a, b) => (b.ran_today_minutes ?? 0) - (a.ran_today_minutes ?? 0));
  const heavyTrucks = allHeavy.filter(v => !ranToday(v)).sort((a, b) => a.equipment_name.localeCompare(b.equipment_name));
  const driverTrucks = vehicles.filter(v => !isHeavy(v)).sort((a, b) => (a.assigned_to?.first_name || a.equipment_name).localeCompare(b.assigned_to?.first_name || b.equipment_name));
  const fmtRan = (min: number) => (min < 60 ? `${min}m` : `${Math.floor(min / 60)}h ${min % 60}m`);

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' as const };
  const chip = (bg: string): React.CSSProperties => ({ background: bg, color: '#fff', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' });

  const renderRow = (v: Vehicle, showRan = false) => (
    <div key={v.equipment_id} onClick={() => openDrawer(v.equipment_id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ width: 12, height: 12, borderRadius: '50%', background: STATUS_COLOR[v.status], flexShrink: 0, boxShadow: v.is_hauler ? '0 0 0 2px #ea580c' : 'none' }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 16, color: '#0f172a' }}>{v.assigned_to?.first_name || v.equipment_name}</strong>
          {v.assigned_to
            ? <span style={{ fontSize: 12, color: '#64748b' }}>{v.equipment_name}{v.bradanna_equipment_number ? ` #${v.bradanna_equipment_number}` : ''}</span>
            : v.bradanna_equipment_number && <span style={{ fontSize: 12, color: '#64748b' }}>#{v.bradanna_equipment_number}</span>}
          {v.has_camera && <Camera size={15} style={{ color: '#0891b2' }} />}
        </div>
        {v.current_job && <div style={{ fontSize: 13, fontWeight: 600, color: '#059669', marginTop: 2 }}>📍 {v.current_job.job_name}</div>}
        {!v.current_job && v.current_place && <div style={{ fontSize: 13, fontWeight: 600, color: '#0891b2', marginTop: 2 }}>📍 {v.current_place.name}</div>}
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <MapPin size={11} style={{ display: 'inline', verticalAlign: -1 }} /> {v.address || (v.lat != null ? `${v.lat.toFixed(4)}, ${v.lng!.toFixed(4)}` : 'No location')}
        </div>
        {v.loaded_equipment && v.loaded_equipment.length > 0 && (
          <div style={{ fontSize: 12, marginTop: 2, color: '#d97706', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>🚛 Hauling: {v.loaded_equipment.map(m => m.equipment_name).join(', ')}</div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontWeight: 700, color: STATUS_COLOR[v.status], textTransform: 'capitalize', fontSize: 13 }}>{v.status}{v.status === 'moving' && v.speed_mph != null ? ` · ${v.speed_mph}` : ''}</div>
        {showRan && (v.ran_today_minutes ?? 0) > 0 && <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>🕒 {fmtRan(v.ran_today_minutes!)} today</div>}
        {showRan && v.ran_today_minutes == null && inUseNow(v) && <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>🕒 In use now</div>}
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{formatAge(v.synced_at)}</div>
      </div>
    </div>
  );

  const sectionHeader = (label: string) => (
    <div style={{ padding: '9px 14px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>{label}</div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '8px 16px 16px', maxWidth: 760, margin: '0 auto' }}>
      {/* Totals + refresh + map toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, fontSize: 13, flexWrap: 'wrap' }}>
        {totals ? (
          <>
            <span><strong style={{ fontSize: 17 }}>{totals.count}</strong> trucks</span>
            <span style={{ color: '#10b981' }}>· <strong style={{ fontSize: 17, color: '#10b981' }}>{totals.moving}</strong> moving</span>
            {totals.idle > 0 && <span style={{ color: '#d97706' }}>· <strong style={{ fontSize: 17, color: '#d97706' }}>{totals.idle}</strong> idle</span>}
          </>
        ) : <span style={{ color: '#94a3b8' }}>Loading…</span>}
        <button onClick={() => load()} disabled={loading} aria-label="Refresh" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex' }}>
          <RefreshCw size={20} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
        </button>
        <button onClick={() => setTab(tab === 'map' ? 'list' : 'map')} aria-label="Toggle map" style={{ background: tab === 'map' ? '#ea580c' : 'none', color: tab === 'map' ? '#fff' : '#0f172a', border: tab === 'map' ? 'none' : '1px solid #e5e7eb', cursor: 'pointer', padding: 6, display: 'inline-flex', borderRadius: 6, marginLeft: 4 }}>
          {tab === 'map' ? <ListIcon size={20} /> : <MapIcon size={20} />}
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fee', borderRadius: 8, color: '#900', marginBottom: 12 }}>{error}</div>}
      {loading && vehicles.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading…</div>}

      {/* List */}
      <div style={{ display: tab === 'list' ? 'flex' : 'none', flexDirection: 'column', gap: 14 }}>
        {activeHaulers.length > 0 && (
          <section style={{ ...card, border: '1px solid #fdba74', boxShadow: '0 1px 3px rgba(234,88,12,0.12)' }}>
            {sectionHeader(`🚛 Active Today · ${activeHaulers.length}`)}
            {activeHaulers.map(v => renderRow(v, true))}
          </section>
        )}
        {driverTrucks.length > 0 && (
          <section style={card}>{sectionHeader('Trucks')}{driverTrucks.map(v => renderRow(v))}</section>
        )}
        {heavyTrucks.length > 0 && (
          <section style={card}>{sectionHeader('Haulers & Heavy Trucks')}{heavyTrucks.map(v => renderRow(v))}</section>
        )}
        {!loading && vehicles.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: '#888', ...card }}>No trucks found.</div>}
      </div>

      {/* Map */}
      <div style={{ display: tab === 'map' ? 'block' : 'none', position: 'relative', height: 'calc(100dvh - 220px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))', minHeight: 300, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        {resuming && (
          <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 5, background: 'rgba(15,23,42,0.9)', color: '#fff', padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            <span style={{ width: 10, height: 10, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
            Updating live positions…
          </div>
        )}
      </div>

      {/* Detail — collapsed bar (so the map/route stays visible). Taller "trip
          browser": tap the header to open the full card; load trips inline and
          scrub through them with a slider that shows each trip's locations. */}
      {selected && sheetCollapsed && (() => {
        const cur = trips && selectedTripIndex != null ? trips.trips[selectedTripIndex] : null;
        const nTrips = trips?.trips.length ?? 0;
        const stepBtn: React.CSSProperties = { width: 30, height: 30, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: 18, lineHeight: 1 };
        return (
        <div ref={collapsedBarRef} style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))', zIndex: 99, display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 14px 12px', background: '#fff', borderTop: '1px solid #e5e7eb', boxShadow: '0 -4px 16px rgba(0,0,0,0.18)' }}>
          {/* Header row — tap to open the full drawer */}
          <div onClick={() => setSheetCollapsed(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <span style={{ fontSize: 18 }}>🚚</span>
            <strong style={{ fontSize: 16, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.assigned_to?.first_name || selected.equipment_name}</strong>
            {(selected.current_job || selected.current_place) && <span style={{ fontSize: 12, color: '#0891b2', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {selected.current_job?.job_name || selected.current_place?.name}</span>}
            {selected.has_camera && selected.samsara_vehicle_id && (
              <button onClick={(e) => { e.stopPropagation(); openCameras(selected); }} disabled={camerasLoading} title="Live cameras" style={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                <Camera size={15} /> {camerasLoading ? '…' : 'Cameras'}
              </button>
            )}
            <span style={{ marginLeft: selected.has_camera && selected.samsara_vehicle_id ? 8 : 'auto', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 12 }}>Details <ChevronUp size={16} /></span>
          </div>

          {runActive ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>🔵 On a run{runFrom ? ` · left ${runFrom}` : ''}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Trail follows the truck live</div>
              </div>
              <button onClick={hideCurrentRun} style={{ flexShrink: 0, padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Hide</button>
            </div>
          ) : !trips ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {(selected.status === 'moving' || selected.status === 'idle') && (
                <button onClick={showCurrentRun} disabled={runLoading} style={{ flex: 1, padding: '11px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
                  <MapPin size={16} /> {runLoading ? 'Loading…' : 'Show current run'}
                </button>
              )}
              <button onClick={() => loadTrips(selected)} disabled={tripsLoading} style={{ flex: 1, padding: '11px', background: '#ea580c', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer' }}>
                <RouteIcon size={16} /> {tripsLoading ? 'Loading…' : "Today's trips"}
              </button>
            </div>
          ) : nTrips === 0 ? (
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', padding: '4px 0' }}>No trips recorded today.</div>
          ) : (
            <>
              {/* Current trip's locations (the 3–4 readable lines) — tap to open full card */}
              <div onClick={() => setSheetCollapsed(false)} style={{ cursor: 'pointer', minHeight: 38 }}>
                {cur ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-start', gap: 6, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      <span style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur.from || '—'}</span>
                      <span style={{ flexShrink: 0, color: '#94a3b8' }}>→</span>
                      <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur.to || '—'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{formatClock(cur.start_time)}–{formatClock(cur.end_time)} · {cur.distance_miles} mi · {formatDur(cur.duration_min)}</div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>All {nTrips} trips · {trips.total_distance_miles} mi · {formatDur(trips.total_drive_min)} driving<div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginTop: 2 }}>Slide to step through each trip →</div></div>
                )}
              </div>
              {/* Trip slider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => stepTrip(-1)} title="Previous trip" style={stepBtn}>‹</button>
                <input type="range" min={0} max={nTrips - 1} step={1} value={selectedTripIndex ?? 0} onChange={e => selectTrip(Number(e.target.value))} style={{ flex: 1, minWidth: 0, accentColor: '#ea580c', height: 16 }} />
                <button onClick={() => stepTrip(1)} title="Next trip" style={stepBtn}>›</button>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#ea580c', minWidth: 34, textAlign: 'center', flexShrink: 0 }}>{selectedTripIndex != null ? `${selectedTripIndex + 1}/${nTrips}` : `–/${nTrips}`}</span>
                {selectedTripIndex != null && <button onClick={() => selectTrip(null)} title="Show all trips" style={{ padding: '6px 9px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>All</button>}
                <button onClick={backToLive} title="Back to live tracking" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 9px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}><span style={{ fontSize: 8 }}>●</span> Live</button>
              </div>
            </>
          )}
        </div>
        );
      })()}

      {/* Detail bottom sheet */}
      {selected && !sheetCollapsed && (
        <Portal>
          <div onClick={closeDrawer} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
          <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '85vh', overflowY: 'auto', background: '#fff', borderRadius: '16px 16px 0 0', zIndex: 301, padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -8px 24px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>{selected.assigned_to?.first_name ? `${selected.assigned_to.first_name} · ${selected.equipment_name}` : selected.equipment_name}</h2>
                <div style={{ fontSize: 12, color: '#64748b' }}>{selected.equipment_type}{selected.bradanna_equipment_number ? ` · #${selected.bradanna_equipment_number}` : ''}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span style={chip('#0891b2')}>{SOURCE_LABEL[selected.source]}</span>
                  {selected.is_hauler && <span style={chip('#ea580c')}>HAULER</span>}
                  <span style={chip(STATUS_COLOR[selected.status])}>{selected.status.toUpperCase()}</span>
                </div>
              </div>
              <button onClick={() => setSheetCollapsed(true)} title="Minimize" style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}><ChevronDown size={18} /></button>
              <button onClick={closeDrawer} title="Close" style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer' }}><X size={18} /></button>
            </div>

            {/* Assignment — only super admins can change it; others see the name */}
            <div style={{ ...card, padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Assigned to</span>
              {isSuperAdmin ? (
                <select value={selected.assigned_to?.employee_id ?? ''} disabled={assignSaving}
                  onChange={(e) => assignTruck(selected, e.target.value ? Number(e.target.value) : null)}
                  style={{ marginLeft: 'auto', maxWidth: 220, padding: '6px 8px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <option value="">— Unassigned —</option>
                  {employees.map(emp => <option key={emp.employee_id} value={emp.employee_id}>{emp.first_name} {emp.last_name}</option>)}
                </select>
              ) : (
                <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#0f172a' }}>{selected.assigned_to?.name || 'Unassigned'}</span>
              )}
            </div>

            {/* Location */}
            <div style={{ ...card, padding: 12, marginBottom: 12, fontSize: 13 }}>
              {[
                ...(selected.current_place && !selected.current_job ? [['Parked at', selected.current_place.name]] : []),
                ['At job', selected.current_job ? selected.current_job.job_name : 'Off-site'],
                ['Location', selected.address || (selected.lat != null ? `${selected.lat.toFixed(5)}, ${selected.lng!.toFixed(5)}` : '—')],
                ['Speed', selected.speed_mph != null ? `${selected.speed_mph} mph` : '—'],
                ['Last update', formatAge(selected.synced_at)],
                ['Tracked by', selected.position_source === 'traccar' ? 'GPS tracker (live)' : selected.position_source === 'samsara' ? 'Samsara (polled)' : '—'],
              ].map(([k, val]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                  <span style={{ color: '#64748b' }}>{k}</span><span style={{ fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>

            {/* On the trailer */}
            {selected.is_hauler && (
              <div style={{ ...card, padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>🚛 On the trailer</div>
                {selected.loaded_equipment && selected.loaded_equipment.length > 0
                  ? selected.loaded_equipment.map(m => (
                    <div key={m.equipment_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontWeight: 600 }}>{m.equipment_name}</span>
                      <span style={{ color: '#64748b' }}>{m.bradanna_equipment_number ? `#${m.bradanna_equipment_number}` : m.equipment_type}</span>
                    </div>))
                  : <div style={{ fontSize: 12, color: '#64748b' }}>{selected.source === 'samsara' ? 'Needs a GPS tracker on this hauler to sense its load.' : 'Nothing detected right now.'}</div>}
              </div>
            )}

            {/* Samsara actions */}
            {selected.samsara_vehicle_id && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                {selected.has_camera && (
                  <button onClick={() => loadCameras(selected)} disabled={camerasLoading} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Camera size={16} /> {camerasLoading ? 'Loading…' : 'Cameras'}
                  </button>
                )}
                <button onClick={() => loadLiveShare(selected)} disabled={liveShareLoading} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <ExternalLink size={16} /> {liveShareLoading ? '…' : 'Live Share'}
                </button>
              </div>
            )}
            {cameras && (
              <div style={{ marginBottom: 12 }}>
                {cameras.length === 0 ? <div style={{ fontSize: 13, color: '#64748b' }}>No recent camera media.</div> : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {cameras.map((c, i) => (
                      <img
                        key={i}
                        src={c.url}
                        alt={c.input || `cam ${i}`}
                        onClick={() => {
                          const withUrl = cameras.filter(x => x.url);
                          setLightbox({ photos: withUrl.map(x => x.url), index: Math.max(0, withUrl.findIndex(x => x.url === c.url)), captions: withUrl.map(photoCaption) });
                        }}
                        style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Trips */}
            <div style={{ ...card, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <RouteIcon size={16} style={{ color: '#ea580c' }} />
                <strong style={{ color: '#0f172a' }}>Trips</strong>
                <input type="date" value={tripDate} max={todayEstYmd()} onChange={e => setTripDate(e.target.value)} style={{ marginLeft: 'auto', padding: '4px 8px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8 }} />
                <button onClick={() => loadTrips(selected)} disabled={tripsLoading} style={{ padding: '6px 12px', background: '#ea580c', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600 }}>{tripsLoading ? '…' : 'Show'}</button>
              </div>
              {trips && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{trips.point_count === 0 ? 'No GPS data for this day.' : `${trips.total_distance_miles} mi · ${formatDur(trips.total_drive_min)} driving · ${trips.trips.length} trips`}</span>
                    {selectedTripIndex != null && <button onClick={() => selectTrip(null)} style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}>Show all</button>}
                  </div>
                  {trips.trips.map((t, i) => {
                    const sel = selectedTripIndex === i;
                    return (
                      <div key={i} onClick={() => selectTrip(sel ? null : i)} style={{ padding: '8px 10px', marginTop: 4, fontSize: 12, cursor: 'pointer', borderRadius: 8, border: '1px solid ' + (sel ? '#ea580c' : '#e5e7eb'), background: sel ? 'rgba(234,88,12,0.08)' : '#fff' }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>Trip {i + 1}: {t.from || '—'} → {t.to || '—'}</div>
                        <div style={{ color: '#64748b' }}>{formatClock(t.start_time)}–{formatClock(t.end_time)} · {t.distance_miles} mi · {formatDur(t.duration_min)} · max {Math.round(t.max_speed_mph)} mph</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </Portal>
      )}
      {lightbox && (
        <PhotoLightbox photos={lightbox.photos} startIndex={lightbox.index} captions={lightbox.captions} onClose={() => setLightbox(null)} />
      )}
      {popup && (
        <Portal>
          <div onClick={() => setPopup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', maxWidth: 320, textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600 }}>{popup}</div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>Tap anywhere to dismiss</div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
